// Shared Skills Management view components (Feature #288).
//
// Feature #309 retired `SkillProfileTable` — the "Current profile" table was
// replaced by a core competency radar and `StrengthSpine`; Feature #355 then
// retired the radar with the core competencies themselves, leaving the
// spine. The badges, delta list and the shared table/panel styles below are
// still used by both person pages and by the review screens.
//
// Feature #320 retired `HistoryList` — the flat name/number dump at the
// bottom of a profile is now `AssessmentFeed`, with `HistoryRecordTable`
// below as its audit fallback. The rating/direction/state atoms the feed is
// built from live here so the feed, the banner and the record table cannot
// drift apart.
import React, { useEffect, useState } from 'react'
import { formatDateTime } from '@/utils/dateFormat'
import {
  getRubric,
  SmDelta,
  SmHistoryOutcome,
  SmHistoryReview,
  SmHistorySubmission,
  SmRubricLevel,
} from '@/api/sm'

export const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '10px 12px',
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--color-dark-grey)',
  borderBottom: '1px solid var(--color-light-grey)',
  textTransform: 'uppercase',
}

export const td: React.CSSProperties = {
  padding: '10px 12px',
  fontSize: '14px',
  borderBottom: '1px solid var(--color-light-grey)',
  verticalAlign: 'top',
}

/** The card chrome the two Feature #309 visualisation blocks sit in. */
export const blockStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-white)',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '12px',
  padding: '22px 24px',
}

/** Pill-group container for the Chart/Table and sort toggles. */
export const toggleGroup: React.CSSProperties = {
  display: 'inline-flex',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '999px',
  overflow: 'hidden',
}

export const toggleButton = (active: boolean): React.CSSProperties => ({
  border: 'none',
  background: active ? 'var(--color-magenta)' : 'transparent',
  color: active ? 'var(--color-white)' : 'var(--color-dark-grey)',
  fontFamily: 'var(--font-family)',
  fontSize: '12px',
  minHeight: 'auto',
  borderRadius: 0,
  padding: '6px 14px',
  cursor: 'pointer',
})

export const kpiValue: React.CSSProperties = {
  fontSize: '26px',
  fontWeight: 600,
  lineHeight: 1.1,
  fontVariantNumeric: 'tabular-nums',
}

export const kpiLabel: React.CSSProperties = {
  fontSize: '11px',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'var(--color-dark-grey)',
}

// The rubric is small, static and needed by both Feature #309 blocks. Cache
// the in-flight promise at module scope so a page rendering both blocks
// issues one request, not two. A failure clears the cache so the next mount
// retries.
let rubricRequest: Promise<SmRubricLevel[]> | null = null

/**
 * Rubric levels. Since #355 the API serves the technical set only; call
 * sites still filter on `skill_type === 'technical'`, which stays correct
 * and costs nothing.
 */
export const useRubric = (): SmRubricLevel[] => {
  const [levels, setLevels] = useState<SmRubricLevel[]>([])
  useEffect(() => {
    let active = true
    if (!rubricRequest) rubricRequest = getRubric()
    rubricRequest
      .then((data) => {
        if (active) setLevels(data)
      })
      .catch(() => {
        rubricRequest = null
      })
    return () => {
      active = false
    }
  }, [])
  return levels
}

export const RatingBadge: React.FC<{ rating?: number | null; label?: string | null }> = ({
  rating,
  label,
}) => {
  if (!rating) return <span style={{ color: 'var(--color-dark-grey)' }}>—</span>
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: '10px',
        backgroundColor: '#f3e8f1',
        color: 'var(--color-magenta)',
        fontSize: '13px',
        fontWeight: 600,
      }}
    >
      {rating}
      {label ? ` · ${label}` : ''}
    </span>
  )
}

/**
 * A rating rendered as a numeral on its ramp fill (Feature #320).
 *
 * This is the first thing in SM to print text on a ramp step, which is why
 * `--sm-level-ink-N` exists — the switchover from dark ink to white sits
 * between levels 2 and 3 on the light ramp and between 3 and 4 on the dark
 * one, and both were measured rather than eyeballed. Never hard-code a
 * foreground here.
 *
 * `null` renders the "no rating yet" slot as a dashed outline, deliberately
 * distinct from level 1 — an unrated skill is not a weak one.
 */
export const LevelToken: React.FC<{
  level?: number | null
  /** Ring the token to mark it as the value that became the new rating. */
  emphasis?: boolean
  title?: string
}> = ({ level, emphasis = false, title }) => {
  const base: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '25px',
    height: '25px',
    borderRadius: '6px',
    fontSize: '12.5px',
    fontWeight: 700,
    flex: 'none',
    fontVariantNumeric: 'tabular-nums',
  }
  if (level == null) {
    return (
      <span
        style={{
          ...base,
          border: '1px dashed var(--color-light-grey)',
          color: 'var(--color-dark-grey)',
          fontWeight: 500,
        }}
        title={title}
      >
        –
      </span>
    )
  }
  return (
    <span
      style={{
        ...base,
        backgroundColor: `var(--sm-level-${level})`,
        color: `var(--sm-level-ink-${level})`,
        boxShadow: emphasis
          ? '0 0 0 2px var(--color-white), 0 0 0 4px var(--color-black)'
          : undefined,
      }}
      title={title}
    >
      {level}
    </span>
  )
}

/** An uppercase caption above a level token — "current", "proposed", "agreed". */
export const LabelledLevel: React.FC<{
  caption: string
  level?: number | null
  emphasis?: boolean
  title?: string
}> = ({ caption, level, emphasis, title }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
    <span
      style={{
        fontSize: '10.5px',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        color: 'var(--color-dark-grey)',
        fontWeight: 700,
      }}
    >
      {caption}
    </span>
    <LevelToken level={level} emphasis={emphasis} title={title} />
  </span>
)

/**
 * Direction of an actual rating change. Rendered ONLY where a rating really
 * moved — a first rating or a review outcome. A delta is a proposal, so it
 * gets a neutral `StateChip` instead; colouring it would read as a verdict
 * the review has not reached yet.
 */
export const DirectionChip: React.FC<{ from: number; to: number }> = ({ from, to }) => {
  const change = to - from
  if (change === 0) {
    return (
      <span style={{ fontSize: '12.5px', color: 'var(--color-dark-grey)' }}>
        rating held
      </span>
    )
  }
  const magnitude = Math.abs(change)
  return (
    <span
      style={{
        fontSize: '12.5px',
        fontWeight: 700,
        whiteSpace: 'nowrap',
        color: change > 0 ? 'var(--sm-direction-up)' : 'var(--sm-direction-down)',
      }}
    >
      {change > 0 ? '▲' : '▼'} {magnitude} level{magnitude > 1 ? 's' : ''}
    </span>
  )
}

/**
 * Delta lifecycle and item state. Neutral chrome with a glyph, never a
 * status colour: an open delta is a disagreement awaiting a conversation,
 * not an error, and the warning palette is reserved for things that are.
 */
export const StateChip: React.FC<{
  children: React.ReactNode
  tone?: 'neutral' | 'open' | 'muted'
}> = ({ children, tone = 'neutral' }) => {
  const tones: Record<string, React.CSSProperties> = {
    neutral: { borderColor: 'var(--color-light-grey)', color: 'var(--color-black)' },
    open: { borderColor: '#c9b0c0', color: 'var(--color-magenta)', backgroundColor: '#fdf2f8' },
    muted: {
      borderColor: 'var(--color-light-grey)',
      color: 'var(--color-dark-grey)',
      borderStyle: 'dashed',
    },
  }
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        fontSize: '12px',
        fontWeight: 600,
        border: '1px solid',
        borderRadius: '999px',
        padding: '2px 9px',
        whiteSpace: 'nowrap',
        backgroundColor: 'var(--color-white)',
        ...tones[tone],
      }}
    >
      {children}
    </span>
  )
}

/** Event-type chip — the label carries identity, the colour only supports it. */
export const EventChip: React.FC<{ kind: 'self' | 'peer' | 'review'; children: React.ReactNode }> = ({
  kind,
  children,
}) => {
  const tones: Record<string, React.CSSProperties> = {
    self: { borderColor: '#e3c3d6', color: 'var(--color-magenta)', backgroundColor: '#fdf2f8' },
    peer: { borderColor: '#cbd5e1', color: '#334155', backgroundColor: '#f8fafc' },
    review: { borderColor: '#9ca3af', color: 'var(--color-black)', backgroundColor: '#f3f4f6' },
  }
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '12px',
        fontWeight: 600,
        border: '1px solid',
        borderRadius: '999px',
        padding: '2.5px 10px',
        ...tones[kind],
      }}
    >
      {children}
    </span>
  )
}

export const StaleBadge: React.FC = () => (
  <span
    style={{
      display: 'inline-block',
      marginLeft: '8px',
      padding: '2px 8px',
      borderRadius: '10px',
      backgroundColor: '#fef3c7',
      color: '#92400e',
      fontSize: '12px',
      fontWeight: 600,
    }}
  >
    Stale
  </span>
)

export const DeltaList: React.FC<{
  deltas: SmDelta[]
  showSubject?: boolean
}> = ({ deltas, showSubject = false }) => (
  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
    <thead>
      <tr>
        {showSubject && <th style={th}>Person</th>}
        <th style={th}>Skill</th>
        <th style={th}>Change</th>
        <th style={th}>Proposed by</th>
        <th style={th}>When</th>
        <th style={th}>State</th>
      </tr>
    </thead>
    <tbody>
      {deltas.map((d) => (
        <tr key={d.id}>
          {showSubject && <td style={td}>{d.user_display_name || d.user_id}</td>}
          <td style={td}>{d.skill_name || d.skill_id}</td>
          <td style={td}>
            {d.previous_rating ?? '—'} → <strong>{d.proposed_rating}</strong>
            {d.agreed_rating != null && (
              <span style={{ color: 'var(--color-dark-grey)' }}> (agreed {d.agreed_rating})</span>
            )}
          </td>
          <td style={td}>{d.proposed_by_display_name || d.proposed_by_user_id}</td>
          <td style={{ ...td, color: 'var(--color-dark-grey)', fontSize: '13px' }}>
            {formatDateTime(d.proposed_at)}
          </td>
          <td style={{ ...td, textTransform: 'capitalize' }}>{d.state.replace('_', ' ')}</td>
        </tr>
      ))}
    </tbody>
  </table>
)

const OUTCOME_LABEL: Record<SmHistoryOutcome, string> = {
  first_rated: 'First rated — applied',
  confirmed: 'Confirmed',
  delta_opened: 'Delta opened',
}

/**
 * The complete record, one row per rating (Feature #320).
 *
 * This is the audit and accessibility fallback behind the change feed — the
 * same role the Chart/Table toggle played on the #309 radar. It replaced
 * `HistoryList`, which wrapped every rating in the profile as prose chips
 * and was the reason the block read as "a splattering of words and numbers".
 * A real table costs ~40% less height and can be scanned down a column.
 *
 * Everything is here on purpose: unlike the feed, nothing is collapsed,
 * because "show me exactly what was submitted" is the question this view
 * exists to answer.
 */
export const HistoryRecordTable: React.FC<{
  submissions: SmHistorySubmission[]
  reviews: SmHistoryReview[]
}> = ({ submissions, reviews }) => {
  if (submissions.length === 0 && reviews.length === 0) {
    return <p style={{ color: 'var(--color-dark-grey)' }}>No history yet.</p>
  }
  return (
    <div style={{ display: 'grid', gap: '22px' }}>
      {reviews.map((r) => (
        <div key={r.review_id}>
          <div style={{ fontWeight: 600, marginBottom: '8px' }}>
            Review — {r.status === 'resolved' ? 'completed' : 'in progress'}{' '}
            <span style={{ color: 'var(--color-dark-grey)', fontWeight: 400, fontSize: '13px' }}>
              opened {formatDateTime(r.opened_at)}
              {r.resolved_at ? `, resolved ${formatDateTime(r.resolved_at)}` : ''}
              {/* #372 */}
              {(r.conducted_by_display_name || r.conducted_by_user_id) &&
                ` · conducted by ${r.conducted_by_display_name || r.conducted_by_user_id}`}
            </span>
          </div>
          {/* #364 — the outcome per skill first, attributed to the review
              and not to any proposer, then whichever proposals the caller
              may see. For the subject the second table holds only their
              own; a skill in the first with nothing in the second was
              raised by somebody else. */}
          {(r.outcomes || []).length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '8px' }}>
              <thead>
                <tr>
                  <th style={th}>Skill</th>
                  <th style={th}>Was</th>
                  <th style={th}>Agreed</th>
                  <th style={th}>State</th>
                  <th style={th}>Note</th>
                </tr>
              </thead>
              <tbody>
                {r.outcomes.map((o) => (
                  <tr key={o.skill_id}>
                    <td style={td}>{o.skill_name || o.skill_id}</td>
                    <td style={td}>{o.previous_rating ?? '—'}</td>
                    <td style={td}>
                      {o.agreed_rating != null ? <strong>{o.agreed_rating}</strong> : '—'}
                    </td>
                    <td style={{ ...td, color: 'var(--color-dark-grey)' }}>
                      {o.agreed_rating != null
                        ? 'Agreed'
                        : r.status === 'resolved'
                        ? 'Returned to open'
                        : 'In review'}
                    </td>
                    {/* #372 — the note recorded against the agreed rating. */}
                    <td style={{ ...td, color: 'var(--color-dark-grey)' }}>{o.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {r.deltas.length > 0 && <DeltaList deltas={r.deltas} />}
          {r.meeting_notes && (
            <p style={{ marginTop: '8px', fontSize: '13px', color: 'var(--color-dark-grey)' }}>
              Meeting notes: {r.meeting_notes}
            </p>
          )}
        </div>
      ))}
      {submissions.map((s) => (
        <div key={s.submission_id}>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: '10px',
              flexWrap: 'wrap',
              marginBottom: '8px',
            }}
          >
            <EventChip kind={s.assessment_type}>
              {s.assessment_type === 'peer' ? 'Peer' : 'Self'} assessment by{' '}
              {s.assessor_display_name || s.assessor_user_id}
            </EventChip>
            <span style={{ color: 'var(--color-dark-grey)', fontSize: '13px' }}>
              {formatDateTime(s.submitted_at)} · {s.items.length} rating
              {s.items.length === 1 ? '' : 's'}
            </span>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Skill</th>
                <th style={th}>Submitted</th>
                <th style={th}>Rating then in effect</th>
                <th style={th}>Outcome</th>
                <th style={th}>Comment</th>
              </tr>
            </thead>
            <tbody>
              {s.items.map((item) => (
                <tr key={item.skill_id}>
                  <td style={td}>{item.skill_name}</td>
                  <td style={td}>
                    <LevelToken level={item.rating} />
                  </td>
                  <td style={td}>
                    <LevelToken level={item.previous_rating ?? null} />
                  </td>
                  <td style={{ ...td, color: 'var(--color-dark-grey)' }}>
                    {OUTCOME_LABEL[item.outcome] ?? item.outcome}
                  </td>
                  <td style={{ ...td, color: 'var(--color-dark-grey)', fontSize: '13px' }}>
                    {item.comment || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  )
}
